import type { EntityDetail, EntityResponse, EntityStep, TaskDetail } from '../api/client.js';
import {
  collectStepResponseIds,
  renderContentText,
  renderStepText,
  toNumber,
} from './flowchart.js';

export type StepReachability = 'reachable' | 'unreachable' | 'in_cycle' | 'unknown';

/** Where an option came from: the learner's response group, or a step-level response id. */
export type ResponseSource = 'group' | 'step';

export interface TranscriptFieldWarning {
  field: string;
  message: string;
}

export interface TranscriptAltValue {
  text: string;
  raw: string;
  warnings: string[];
}

export interface TranscriptResponse {
  responseId: number;
  rawResponseId: unknown;
  text: string;
  rawText: string;
  isCorrect: boolean | null;
  isCorrectInvalid: boolean;
  responseType: string | null;
  source: ResponseSource;
  /** A step id is only a fallback when response groups also exist on that step. */
  isFallback: boolean;
  /** Wired to the step but absent from `responses[]` — a reference, not an option a learner sees. */
  isMissing: boolean;
  feedback: string | null;
  rawFeedback: string | null;
  altValues: TranscriptAltValue[];
  targetStepId: number | null;
  rawTargetStepId: unknown;
  targetStepText: string | null;
  rawTargetStepText: string | null;
  targetStepIsQuestion: boolean;
  targetStepMissing: boolean;
  /** More than one step carries the routed id, so the destination cannot be named. */
  targetStepAmbiguous: boolean;
  warnings: string[];
  fieldWarnings: TranscriptFieldWarning[];
}

export interface TranscriptStep {
  position: number;
  stepId: number;
  rawStepId: unknown;
  /** What the stored `prompt` renders to. EMPTY means the prompt is empty — not that it is missing. */
  prompt: string;
  rawPrompt: string;
  value: string | null;
  rawValue: string | null;
  /** What the page shows: the prompt, or the value when the prompt renders to nothing. */
  displayText: string;
  isRoot: boolean;
  /** The document's own `is_question` claim. `null` when it does not say. */
  storedIsQuestion: boolean | null;
  storedIsQuestionInvalid: boolean;
  /** Derived: this step has options wired to it. A different claim from `storedIsQuestion`. */
  hasWiredResponses: boolean;
  isTryAgain: boolean;
  responseFormat: string;
  nextStepId: number | null;
  rawNextStepId: unknown;
  nextStepText: string | null;
  rawNextStepText: string | null;
  nextStepMissing: boolean;
  nextStepAmbiguous: boolean;
  videoUrl: string | null;
  videoText: string | null;
  rawVideoText: string | null;
  reachability: StepReachability;
  responses: TranscriptResponse[];
  warnings: string[];
  fieldWarnings: TranscriptFieldWarning[];
}

export interface LessonTranscript {
  kind: 'entity' | 'task';
  title: string;
  rawTitle: string;
  summary: string | null;
  rawSummary: string | null;
  rcText: string | null;
  rawRcText: string | null;
  published: boolean | null;
  publishedInvalid: boolean;
  stepCount: number;
  /** Steps with at least one option marked correct. */
  keyedQuestionCount: number;
  /** Steps offering a real choice (2+ options) with nothing marked correct — the genuine finding. */
  unkeyedQuestionCount: number;
  /** Steps offering exactly one option — an acknowledgement, counted as neither of the above. */
  singleOptionStepCount: number;
  /** What the document itself claims via `is_question`. Measured to be unmaintained; shown, not trusted. */
  explicitlyMarkedQuestionCount: number;
  steps: TranscriptStep[];
  /** Responses no step references. They can still hold a stale key or feedback. */
  orphanResponses: TranscriptResponse[];
  warnings: string[];
  fieldWarnings: TranscriptFieldWarning[];
}

type RawRecord = Record<string, unknown>;

function isRecord(value: unknown): value is RawRecord {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function rawString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint' || typeof value === 'symbol') {
    return String(value);
  }
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return '[unserializable stored value]';
  }
}

function nullableRawString(value: unknown): string | null {
  return value == null ? null : rawString(value);
}

function fieldWarnings(field: string, warnings: string[]): TranscriptFieldWarning[] {
  return warnings.map((message) => ({ field, message }));
}

function warningText(warnings: TranscriptFieldWarning[]): string[] {
  return warnings.map(({ field, message }) => `${field}: ${message}`);
}

/**
 * Text for a WHOLE-LESSON read, which is a different job from the review row's one-line label.
 *
 * `renderContentText` collapses every run of whitespace to a single space. That is right for a
 * truncated label and wrong here: stored CFA content is largely Markdown rather than HTML — measured
 * on the live corpus, 821 of 2,226 sampled step texts carry a blank-line paragraph break and 23
 * carry a Markdown pipe table — so collapsing turns a structured explanation into one run-on
 * paragraph, and a table into a line of words and bars. Where the field contains no HTML tag at all
 * the stored text is therefore kept verbatim, and the shared renderer is still consulted for its
 * loss warnings. Anything containing markup keeps the parsed, flattened path, because rendering
 * stored HTML is an injection surface this page does not need.
 */
function renderTranscriptText(value: unknown): { text: string; warnings: string[] } {
  const shared = renderContentText(value);
  const raw = typeof value === 'string' ? value : '';
  const warnings = [...shared.warnings];
  if (/^[ \t]*\|.*\|[ \t]*$/m.test(raw)) {
    warnings.push('Contains a Markdown table; its rows and columns are shown as stored text.');
  }
  const looksLikeHtml = /<[a-zA-Z!/]/.test(raw);
  if (!raw || looksLikeHtml) {
    return { text: shared.text, warnings };
  }
  // Normalise line endings and strip trailing spaces per line; keep the line structure itself.
  const text = raw.replace(/\r\n?/g, '\n').split('\n').map((line) => line.replace(/[ \t]+$/, '')).join('\n').trim();
  return { text, warnings };
}

function responseGroupIds(step: EntityStep): Set<number> {
  const ids = new Set<number>();
  if (!Array.isArray(step.response_groups)) return ids;
  for (const group of step.response_groups) {
    if (!group || typeof group !== 'object' || !Array.isArray(group.response_ids)) continue;
    for (const rawId of group.response_ids) {
      const id = toNumber(rawId);
      if (id !== null) ids.add(id);
    }
  }
  return ids;
}

/**
 * The recursive walk below overflows the stack at a longest path of roughly four thousand on this
 * runtime (measured: a 3,800-step chain succeeds, 3,900 throws `RangeError`), and browsers differ.
 * A real lesson has tens of steps, so rather than rewrite the walk iteratively we refuse to analyse
 * a graph anywhere near that size and report reachability as `unknown` — an honest absence instead
 * of a crashed page.
 */
const GRAPH_ANALYSIS_MAX_STEPS = 2000;

function findCycles(edges: Array<Set<number>>): Set<number> {
  let nextIndex = 0;
  const indices = new Array<number>(edges.length).fill(-1);
  const lowLinks = new Array<number>(edges.length).fill(-1);
  const stack: number[] = [];
  const onStack = new Set<number>();
  const cyclePositions = new Set<number>();

  const visit = (position: number) => {
    indices[position] = nextIndex;
    lowLinks[position] = nextIndex;
    nextIndex += 1;
    stack.push(position);
    onStack.add(position);

    for (const target of edges[position] ?? []) {
      if (indices[target] === -1) {
        visit(target);
        lowLinks[position] = Math.min(
          lowLinks[position] ?? Number.POSITIVE_INFINITY,
          lowLinks[target] ?? Number.POSITIVE_INFINITY,
        );
      } else if (onStack.has(target)) {
        lowLinks[position] = Math.min(
          lowLinks[position] ?? Number.POSITIVE_INFINITY,
          indices[target] ?? Number.POSITIVE_INFINITY,
        );
      }
    }

    if (lowLinks[position] !== indices[position]) return;
    const component: number[] = [];
    let member: number | undefined;
    do {
      member = stack.pop();
      if (member === undefined) break;
      onStack.delete(member);
      component.push(member);
    } while (member !== position);

    if (component.length > 1 || (component.length === 1 && edges[position]?.has(position))) {
      for (const memberPosition of component) cyclePositions.add(memberPosition);
    }
  };

  for (let position = 0; position < edges.length; position += 1) {
    if (indices[position] === -1) visit(position);
  }
  return cyclePositions;
}

function reachableFrom(root: number, edges: Array<Set<number>>): Set<number> {
  const reached = new Set<number>();
  const pending = [root];
  while (pending.length > 0) {
    const position = pending.pop()!;
    if (reached.has(position)) continue;
    reached.add(position);
    for (const target of edges[position] ?? []) pending.push(target);
  }
  return reached;
}

/**
 * Materialise one current authoring document without graph traversal reordering or omission.
 * All graph operations use stored array positions; ids are only labels used to resolve edges.
 */
export function buildLessonTranscript(
  document: EntityDetail | TaskDetail | Record<string, unknown>,
  kind: 'entity' | 'task',
): LessonTranscript {
  const rawDocument = document as unknown as RawRecord;
  const rawSteps = Array.isArray(rawDocument.steps) ? rawDocument.steps : [];
  const rawResponses = Array.isArray(rawDocument.responses) ? rawDocument.responses : [];
  const invalidStepPositions = new Set(
    rawSteps.map((step, index) => isRecord(step) ? -1 : index).filter((index) => index >= 0),
  );
  const steps = rawSteps.map((step) => isRecord(step) ? step : { prompt: rawString(step) }) as unknown as EntityStep[];
  const responses = rawResponses.map((response) =>
    isRecord(response) ? response : { value: rawString(response) },
  ) as unknown as EntityResponse[];
  const documentWarnings: string[] = [];
  if (rawDocument.steps !== undefined && !Array.isArray(rawDocument.steps)) {
    documentWarnings.push('The stored `steps` field is not an array; this document is malformed, not empty.');
  }
  if (rawDocument.responses !== undefined && !Array.isArray(rawDocument.responses)) {
    documentWarnings.push('The stored `responses` field is not an array; this document is malformed, not empty.');
  }
  const documentFieldWarnings: TranscriptFieldWarning[] = [];

  const stepPositionsById = new Map<number, number[]>();
  steps.forEach((step, position) => {
    const id = toNumber(step.id);
    if (id === null) return;
    const positions = stepPositionsById.get(id) ?? [];
    positions.push(position);
    stepPositionsById.set(id, positions);
  });

  const responsesById = new Map<number, EntityResponse[]>();
  responses.forEach((response) => {
    const id = toNumber(response.id);
    if (id === null) return;
    const matches = responsesById.get(id) ?? [];
    matches.push(response);
    responsesById.set(id, matches);
  });

  const edges = steps.map(() => new Set<number>());
  const addRouteEdges = (from: number, rawTarget: unknown) => {
    const targetId = toNumber(rawTarget);
    if (targetId === null) return;
    for (const targetPosition of stepPositionsById.get(targetId) ?? []) {
      edges[from]!.add(targetPosition);
    }
  };
  steps.forEach((step, position) => {
    addRouteEdges(position, step.next_step_id);
    for (const responseId of collectStepResponseIds(step)) {
      for (const response of responsesById.get(responseId) ?? []) {
        addRouteEdges(position, response.next_step_id);
      }
    }
  });

  const rootPositions = steps
    .map((step, position) => step.is_root === true ? position : -1)
    .filter((position) => position >= 0);
  const rootsAreKnown = rootPositions.length === 1;
  if (steps.length === 0) {
    documentWarnings.push('This document contains no steps.');
  } else if (rootPositions.length === 0) {
    documentWarnings.push('No step declares itself as the root; reachability is unknown.');
  } else if (rootPositions.length > 1) {
    documentWarnings.push('More than one step declares itself as the root; reachability is unknown.');
  }
  // A graph big enough to overflow the recursive cycle walk is analysed as `unknown` rather than
  // crashing the page. No real lesson comes close; the ceiling exists so a pathological one degrades.
  const graphAnalysable = steps.length <= GRAPH_ANALYSIS_MAX_STEPS;
  if (!graphAnalysable) {
    documentWarnings.push(
      `This document stores ${steps.length} steps, beyond the ${GRAPH_ANALYSIS_MAX_STEPS}-step limit for reachability analysis; reachability is not reported.`,
    );
  }
  const analyseGraph = rootsAreKnown && graphAnalysable;
  const reached = analyseGraph ? reachableFrom(rootPositions[0]!, edges) : new Set<number>();
  const cyclePositions = graphAnalysable ? findCycles(edges) : new Set<number>();
  /*
   * A step whose id is shared with another step cannot be given a reachability verdict: a route to
   * that id adds an edge to EVERY position carrying it, so "reachable" would be asserted for all of
   * them when the document establishes it for none. Ambiguity is reported as ambiguity.
   */
  const ambiguousPositions = new Set<number>();
  for (const positions of stepPositionsById.values()) {
    if (positions.length > 1) for (const position of positions) ambiguousPositions.add(position);
  }

  const resolveRoute = (rawTarget: unknown) => {
    const targetStepId = toNumber(rawTarget);
    const targetPositions = targetStepId === null ? [] : (stepPositionsById.get(targetStepId) ?? []);
    const targetStepAmbiguous = targetPositions.length > 1;
    const target = targetPositions.length === 1 ? steps[targetPositions[0]!] : undefined;
    const targetRender = target
      ? (() => {
          const promptText = renderTranscriptText((target as unknown as RawRecord).prompt);
          if (promptText.text) return promptText;
          const valueText = renderTranscriptText((target as unknown as RawRecord).value);
          return { text: valueText.text, warnings: [...promptText.warnings, ...valueText.warnings] };
        })()
      : null;
    const targetRaw = target as unknown as RawRecord | undefined;
    const targetRawText = target
      ? (renderContentText(targetRaw?.prompt).text ? rawString(targetRaw?.prompt) : rawString(targetRaw?.value))
      : null;
    return {
      targetStepId,
      target,
      targetStepText: targetRender?.text ?? null,
      targetRawText,
      targetWarnings: targetRender?.warnings ?? [],
      targetStepMissing: targetStepId !== null && targetPositions.length === 0,
      targetStepAmbiguous,
    };
  };

  const materializeResponse = (
    responseId: number,
    response: EntityResponse | undefined,
    source: ResponseSource,
    isFallback: boolean,
  ): TranscriptResponse => {
    const raw = response as unknown as RawRecord | undefined;
    const renderedValue = renderTranscriptText(raw?.value);
    const renderedFeedback = renderTranscriptText(raw?.feedback);
    const route = resolveRoute(raw?.next_step_id);
    const warnings: TranscriptFieldWarning[] = [
      ...fieldWarnings('Option text', renderedValue.warnings),
      ...fieldWarnings('Feedback', renderedFeedback.warnings),
      ...fieldWarnings('Routed step text', route.targetWarnings),
    ];
    if (!response) {
      warnings.push({ field: 'Response id', message: `Response ${responseId} is wired to this step but is not in the document.` });
    }
    if ((responsesById.get(responseId)?.length ?? 0) > 1) {
      warnings.push({ field: 'Response id', message: `More than one response in this document carries id ${responseId}; showing the first.` });
    }
    if (route.targetStepMissing) {
      warnings.push({ field: 'Route', message: `Route points to step ${route.targetStepId}, which is not in the document.` });
    }
    if (route.targetStepAmbiguous) {
      warnings.push({ field: 'Route', message: `More than one step carries id ${route.targetStepId}; the destination text is ambiguous.` });
    }
    const rawCorrect = raw?.is_correct;
    const isCorrectInvalid = rawCorrect !== undefined && rawCorrect !== null && typeof rawCorrect !== 'boolean';
    if (isCorrectInvalid) {
      warnings.push({ field: 'Answer key', message: 'Invalid stored value; expected true, false, null, or an absent field.' });
    }
    const rawTarget = raw?.next_step_id;
    if (rawTarget !== undefined && rawTarget !== null && toNumber(rawTarget) === null) {
      warnings.push({ field: 'Route', message: 'Invalid stored next_step_id value.' });
    }
    const altValues = Array.isArray(raw?.alt_values)
      ? raw.alt_values.map((value) => {
          const rendered = renderContentText(value);
          return { text: rendered.text, raw: rawString(value), warnings: rendered.warnings };
        })
      : [];
    altValues.forEach((alt, index) => {
      warnings.push(...fieldWarnings(`Alternative value ${index + 1}`, alt.warnings));
    });

    return {
      responseId,
      rawResponseId: raw?.id ?? responseId,
      text: response ? renderedValue.text : `(response ${responseId} is missing from this document)`,
      rawText: response ? rawString(raw?.value) : '',
      isCorrect: typeof rawCorrect === 'boolean' ? rawCorrect : null,
      isCorrectInvalid,
      responseType: typeof raw?.type === 'string' ? raw.type : null,
      source,
      isFallback,
      isMissing: !response,
      feedback: response && renderedFeedback.text ? renderedFeedback.text : null,
      rawFeedback: response && raw?.feedback != null ? rawString(raw.feedback) : null,
      altValues,
      targetStepId: route.targetStepId,
      rawTargetStepId: rawTarget,
      targetStepText: route.targetStepAmbiguous ? null : route.targetStepText,
      rawTargetStepText: route.targetStepAmbiguous ? null : route.targetRawText,
      targetStepIsQuestion: route.target ? collectStepResponseIds(route.target).length > 0 : false,
      targetStepMissing: route.targetStepMissing,
      targetStepAmbiguous: route.targetStepAmbiguous,
      warnings: warningText(warnings),
      fieldWarnings: warnings,
    };
  };

  const referencedResponseIds = new Set<number>();
  const transcriptSteps: TranscriptStep[] = steps.map((step, index) => {
    const raw = step as unknown as RawRecord;
    const stepId = toNumber(raw.id) ?? -1;
    const responseIds = collectStepResponseIds(step);
    responseIds.forEach((id) => referencedResponseIds.add(id));
    const groupedIds = responseGroupIds(step);
    const hasGroups = groupedIds.size > 0;
    const renderedPrompt = renderTranscriptText(raw.prompt);
    const renderedValue = renderTranscriptText(raw.value);
    const renderedVideoText = renderContentText(raw.video_text);
    const ownRoute = resolveRoute(raw.next_step_id);
    const warnings: TranscriptFieldWarning[] = [
      ...fieldWarnings('Prompt', renderedPrompt.warnings),
      ...fieldWarnings('Value', renderedValue.warnings),
      ...fieldWarnings('Video text', renderedVideoText.warnings),
      ...fieldWarnings('Next step text', ownRoute.targetWarnings),
    ];
    if (invalidStepPositions.has(index)) {
      warnings.push({ field: 'Step record', message: 'This stored steps[] element is not an object.' });
    }
    if (toNumber(raw.id) === null) {
      warnings.push({ field: 'Step id', message: 'This step has a missing or invalid stored id.' });
    } else if ((stepPositionsById.get(stepId)?.length ?? 0) > 1) {
      warnings.push({ field: 'Step id', message: `More than one step in this document carries id ${stepId}.` });
    }
    if (ownRoute.targetStepMissing) {
      warnings.push({ field: 'Next step', message: `Route points to step ${ownRoute.targetStepId}, which is not in the document.` });
    }
    if (ownRoute.targetStepAmbiguous) {
      warnings.push({ field: 'Next step', message: `More than one step carries id ${ownRoute.targetStepId}; the destination text is ambiguous.` });
    }
    if (raw.next_step_id !== undefined && raw.next_step_id !== null && toNumber(raw.next_step_id) === null) {
      warnings.push({ field: 'Next step', message: 'Invalid stored next_step_id value.' });
    }
    const rawIsQuestion = raw.is_question;
    const storedIsQuestionInvalid = rawIsQuestion !== undefined && rawIsQuestion !== null && typeof rawIsQuestion !== 'boolean';
    if (storedIsQuestionInvalid) {
      warnings.push({ field: 'is_question', message: 'Invalid stored value; expected true, false, null, or an absent field.' });
    }
    const reachability: StepReachability = !analyseGraph || ambiguousPositions.has(index)
      ? 'unknown'
      : cyclePositions.has(index)
        ? 'in_cycle'
        : reached.has(index) ? 'reachable' : 'unreachable';
    if (reachability === 'unreachable') {
      warnings.push({ field: 'Graph', message: 'Unreachable from the declared root.' });
    } else if (reachability === 'in_cycle') {
      warnings.push({ field: 'Graph', message: 'This step is a member of a cycle.' });
    }

    return {
      position: index + 1,
      stepId,
      rawStepId: raw.id,
      prompt: renderedPrompt.text,
      rawPrompt: rawString(raw.prompt),
      value: raw.value == null ? null : renderedValue.text,
      rawValue: nullableRawString(raw.value),
      displayText: renderedPrompt.text || renderedValue.text,
      isRoot: raw.is_root === true,
      storedIsQuestion: typeof rawIsQuestion === 'boolean' ? rawIsQuestion : null,
      storedIsQuestionInvalid,
      hasWiredResponses: responseIds.length > 0,
      isTryAgain: raw.is_try_again === true,
      responseFormat: typeof raw.response_format === 'string' ? raw.response_format : 'content',
      nextStepId: ownRoute.targetStepId,
      rawNextStepId: raw.next_step_id,
      nextStepText: ownRoute.targetStepAmbiguous ? null : ownRoute.targetStepText,
      rawNextStepText: ownRoute.targetStepAmbiguous ? null : ownRoute.targetRawText,
      nextStepMissing: ownRoute.targetStepMissing,
      nextStepAmbiguous: ownRoute.targetStepAmbiguous,
      videoUrl: typeof raw.video_url === 'string' && raw.video_url.trim() ? raw.video_url : null,
      videoText: renderedVideoText.text || null,
      rawVideoText: nullableRawString(raw.video_text),
      reachability,
      responses: responseIds.map((responseId) => {
        const source: ResponseSource = groupedIds.has(responseId) ? 'group' : 'step';
        return materializeResponse(
          responseId,
          responsesById.get(responseId)?.[0],
          source,
          hasGroups && source === 'step',
        );
      }),
      warnings: warningText(warnings),
      fieldWarnings: warnings,
    };
  });

  const seenReferencedRecord = new Set<number>();
  const orphanResponses = responses.flatMap((response) => {
    const responseId = toNumber(response.id);
    if (responseId !== null && referencedResponseIds.has(responseId)) {
      // The step renders the FIRST record for a wired id. Any further record sharing that id holds
      // text, a key and a route that no step block shows, so it is surfaced here rather than lost.
      if (!seenReferencedRecord.has(responseId)) {
        seenReferencedRecord.add(responseId);
        return [];
      }
      const duplicate = materializeResponse(responseId, response, 'step', false);
      duplicate.fieldWarnings.push({
        field: 'Response id',
        message: `A further stored record also carries id ${responseId}; the step above shows the first one, and this is the other.`,
      });
      duplicate.warnings = warningText(duplicate.fieldWarnings);
      return [duplicate];
    }
    const id = responseId ?? -1;
    const orphan = materializeResponse(id, response, 'step', false);
    if (responseId === null) {
      orphan.fieldWarnings.push({ field: 'Response id', message: 'This orphan response has a missing or invalid stored id.' });
      orphan.warnings = warningText(orphan.fieldWarnings);
    }
    return [orphan];
  });

  const rawPublished = rawDocument.published;
  const publishedInvalid = rawPublished !== undefined && rawPublished !== null && typeof rawPublished !== 'boolean';
  if (publishedInvalid) {
    documentFieldWarnings.push({ field: 'Published', message: 'Invalid stored value; expected true, false, null, or an absent field.' });
  }
  const summarySource = kind === 'entity' ? rawDocument.summary : rawDocument.snippet;
  const renderedTitle = renderContentText(rawDocument.title);
  const renderedSummary = renderTranscriptText(summarySource);
  const renderedRcText = renderTranscriptText(rawDocument.rc_text);
  documentFieldWarnings.push(
    ...fieldWarnings('Title', renderedTitle.warnings),
    ...fieldWarnings(kind === 'entity' ? 'Summary' : 'Snippet', renderedSummary.warnings),
    ...fieldWarnings('Shared reading passage', renderedRcText.warnings),
  );
  documentWarnings.push(...warningText(documentFieldWarnings));

  const explicitlyMarkedQuestions = transcriptSteps.filter((step) => step.storedIsQuestion === true);
  /*
   * These counts are derived from the OPTIONS, not from the stored `is_question` flag, and not from
   * "has any options at all". Both of those were measured against the live corpus on 2026-08-17 and
   * both misreport lesson 1448 — in opposite directions:
   *
   *   - `is_question` is unmaintained. All ELEVEN steps of 1448 store `is_question: false`, including
   *     step 1 and step 10, which are genuine multiple-choice questions with a keyed answer. Trusting
   *     it reports "0 questions" for a lesson that plainly has two, hiding the assessment entirely.
   *     (It is not simply absent either: 395 of 400 sampled documents declare it somewhere, so a
   *     "fall back when undeclared" rule almost never fires.)
   *   - "has wired responses" sweeps in exposition steps. Six of 1448's steps carry a single
   *     `Continue` option whose `is_correct` is absent; counting those reports "6 questions with no
   *     answer key", which reads as six content defects that do not exist.
   *
   * So: a step with an option marked correct HAS a key. A step offering a real choice — two or more
   * options — with nothing marked correct is the genuine finding worth surfacing. A single-option
   * step is an acknowledgement, and is counted as neither. The document's own claim is kept beside
   * these as `explicitlyMarkedQuestionCount`, because its disagreement is itself informative.
   */
  /*
   * Count only what a learner could actually CHOOSE. `step.responses` is the wired record list, and
   * three of its members are not options: a `catchall` is the branch for input matching no option, a
   * step-level fallback beside a response group is not one of the group's choices, and a wired id
   * with no record behind it is a dangling reference. Counting records rather than choices reports a
   * one-option step plus a catchall as "a choice of two", and a step whose single option is keyed as
   * both "keyed" and "a single option to continue, which is not a question" — two contradictory
   * claims about the same step.
   */
  const choosable = (step: TranscriptStep) =>
    step.responses.filter(
      (response) => !response.isMissing && !response.isFallback && response.responseType !== 'catchall',
    );
  const keyedQuestionCount = transcriptSteps.filter((step) =>
    choosable(step).some((response) => response.isCorrect === true),
  ).length;
  const unkeyedQuestionCount = transcriptSteps.filter((step) => {
    const options = choosable(step);
    return options.length > 1 && !options.some((response) => response.isCorrect === true);
  }).length;
  const singleOptionStepCount = transcriptSteps.filter((step) => {
    const options = choosable(step);
    return options.length === 1 && options[0]!.isCorrect !== true;
  }).length;

  return {
    kind,
    title: renderedTitle.text,
    rawTitle: rawString(rawDocument.title),
    summary: summarySource == null ? null : renderedSummary.text,
    rawSummary: nullableRawString(summarySource),
    rcText: rawDocument.rc_text == null ? null : renderedRcText.text,
    rawRcText: nullableRawString(rawDocument.rc_text),
    published: typeof rawPublished === 'boolean' ? rawPublished : null,
    publishedInvalid,
    stepCount: transcriptSteps.length,
    keyedQuestionCount,
    unkeyedQuestionCount,
    singleOptionStepCount,
    explicitlyMarkedQuestionCount: explicitlyMarkedQuestions.length,
    steps: transcriptSteps,
    orphanResponses,
    warnings: documentWarnings,
    fieldWarnings: documentFieldWarnings,
  };
}
